import {
  ActionIcon,
  Flex,
  Grid,
  NumberInput,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconTrash } from "@tabler/icons-react";
import { BulkReceiptItemFormValues } from "pages/Debt/components/BulkReceiptCreateForm/config";
import { ChangeEvent, memo, useCallback } from "react";

export type BulkEditableField = "amount" | "paymentDate" | "notes";

export interface BulkItemErrors {
  amount?: string;
  paymentDate?: string;
}

export interface BulkItemTouched {
  amount?: boolean;
  paymentDate?: boolean;
}

interface BulkReceiptRowProps {
  index: number;
  /** Số thứ tự phiếu thu trong công nợ (1-based), không phải index toàn form. */
  receiptNumber: number;
  item: BulkReceiptItemFormValues;
  itemError?: BulkItemErrors;
  itemTouched?: BulkItemTouched;
  onFieldChange: (
    index: number,
    field: BulkEditableField,
    value: number | "" | Date | null | string,
  ) => void;
  onFieldBlur: (index: number, field: "amount" | "paymentDate") => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

const BulkReceiptRow = memo(
  ({
    index,
    receiptNumber,
    item,
    itemError,
    itemTouched,
    onFieldChange,
    onFieldBlur,
    onRemove,
    canRemove,
  }: BulkReceiptRowProps) => {
    const handleAmountChange = useCallback(
      (value: number | string) =>
        onFieldChange(index, "amount", typeof value === "number" ? value : ""),
      [onFieldChange, index],
    );
    const handleAmountBlur = useCallback(
      () => onFieldBlur(index, "amount"),
      [onFieldBlur, index],
    );
    const handlePaymentDateChange = useCallback(
      (value: Date | null) => onFieldChange(index, "paymentDate", value),
      [onFieldChange, index],
    );
    const handlePaymentDateBlur = useCallback(
      () => onFieldBlur(index, "paymentDate"),
      [onFieldBlur, index],
    );
    const handleNotesChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) =>
        onFieldChange(index, "notes", event.target.value),
      [onFieldChange, index],
    );
    const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);

    return (
      <Stack gap="sm">
        <Flex justify="space-between" align="center">
          <Text size="sm" fw={700} c="blue.7">
            Phiếu thu {receiptNumber}
          </Text>
          <Tooltip label="Xóa dòng" withArrow>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={handleRemove}
              disabled={!canRemove}
              aria-label="Xóa dòng"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Flex>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <NumberInput
              label="Số tiền thu"
              placeholder="Nhập số tiền"
              withAsterisk
              min={0}
              thousandSeparator="."
              decimalSeparator=","
              suffix=" đ"
              value={item.amount}
              onChange={handleAmountChange}
              onBlur={handleAmountBlur}
              error={
                itemTouched?.amount && itemError?.amount
                  ? itemError.amount
                  : null
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DateTimePicker
              label="Ngày thu"
              placeholder="Chọn ngày thu"
              withAsterisk
              value={item.paymentDate}
              onChange={handlePaymentDateChange}
              onBlur={handlePaymentDateBlur}
              error={
                itemTouched?.paymentDate && itemError?.paymentDate
                  ? itemError.paymentDate
                  : null
              }
            />
          </Grid.Col>
        </Grid>

        <Textarea
          label="Ghi chú"
          placeholder="Nhập ghi chú (tuỳ chọn)"
          value={item.notes}
          onChange={handleNotesChange}
          rows={2}
        />
      </Stack>
    );
  },
);

BulkReceiptRow.displayName = "BulkReceiptRow";

export default BulkReceiptRow;
